import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useLoading } from '../../context/LoadingContext';
import {
  getSystemInvoicesByCompany,
  addPaymentToSystemInvoice,
  updateSystemInvoiceStatus,
  getSystemSettings,
  incrementReceiptNumber,
  getCompanyByID
} from '../../services/api/SuperAdminApi';
import ConfirmationModal from '../../components/Modal/ConfirmModal';
import './SystemInvoices.scss';
import { FaPlus, FaFilter } from 'react-icons/fa';
import ReactDOM from 'react-dom/client';
import { Systeminvoicepdftemplate } from '../Systeminvoicepdftemplate/Systeminvoicepdftemplate';
import { SystemReceiptPdfTemplate } from '../SystemReceiptPdfTemplate/SystemReceiptPdfTemplate';
import systemInvoiceHeader from '../../assets/images/system_invoice_header.jpeg';

export const SystemInvoices = ({ companyId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showLoading, hideLoading } = useLoading();

  const [invoices, setInvoices] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('');
  const [selectedInvoiceTotal, setSelectedInvoiceTotal] = useState(0);
  const [selectedInvoicePayments, setSelectedInvoicePayments] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [cancelledRem, setCancelledRem] = useState('');

  // Filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    minAmount: '',
    maxAmount: '',
    fromDate: '',
    toDate: '',
  });

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        //showLoading('Loading invoices...');

        // Fetch both company data and invoices
        const [companyResponse, invoicesResponse] = await Promise.all([
          getCompanyByID(companyId),
          getSystemInvoicesByCompany(companyId)
        ]);

        console.log('Invoices Response:', invoicesResponse);
        console.log('Invoices Data:', invoicesResponse.data);

        setCompanyData(companyResponse.data);

        // Use filteredInvoice from response
        const invoicesData = invoicesResponse.data?.filteredInvoice || [];

        console.log('Filtered Invoices:', invoicesData);
        setInvoices(invoicesData);

        //hideLoading();
      } catch (error) {
        console.error('Error fetching data:', error);
        showToast('Failed to fetch data', 'error');
        setInvoices([]); // Set empty array on error
        //hideLoading();
      }
    };

    if (companyId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Refetch invoices when filters change
  useEffect(() => {
    if (companyId) {
      fetchInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchInvoices = async () => {
    try {
      showLoading('Loading invoices...');
      
      // Build query parameters from filters
      const queryParams = new URLSearchParams();
      
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.minAmount) {
        queryParams.append('minAmount', filters.minAmount);
      }
      if (filters.maxAmount) {
        queryParams.append('maxAmount', filters.maxAmount);
      }
      if (filters.fromDate) {
        queryParams.append('fromDate', filters.fromDate);
      }
      if (filters.toDate) {
        queryParams.append('toDate', filters.toDate);
      }

      const queryString = queryParams.toString();
      const url = queryString ? `${companyId}?${queryString}` : companyId;
      
      const response = await getSystemInvoicesByCompany(url);
      console.log('System invoices response:', response);
      console.log('System invoices data:', response.data);

      // Use filteredInvoice from response
      const invoicesData = response.data?.filteredInvoice || [];

      setInvoices(invoicesData);
      hideLoading();
    } catch (error) {
      console.error('Error fetching system invoices:', error);
      showToast('Failed to fetch invoices', 'error');
      setInvoices([]); // Set empty array on error
      hideLoading();
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      paid: 'status-paid',
      pending: 'status-pending',
      'part-payment': 'status-part-payment',
      cancelled: 'status-cancelled',
      overdue: 'status-overdue'
    };
    return statusMap[status] || 'status-pending';
  };

  const handleStatusUpdateClick = async (invoiceId) => {
    try {
      console.log('Opening status modal for invoice:', invoiceId);

      // Find the invoice from existing state instead of API call
      const invoice = invoices.find(inv => inv._id === invoiceId);

      console.log('Found invoice:', invoice);

      if (!invoice) {
        showToast('Invoice not found', 'error');
        return;
      }

      // Set the invoice data for the modal
      setSelectedInvoiceId(invoice._id);
      setSelectedInvoiceStatus(invoice.status);
      setSelectedInvoiceTotal(invoice.totalAmount);
      setSelectedInvoicePayments(invoice.payments || []);
      setCancelledRem(invoice.cancelRemark || '');

      // Open the modal
      setStatusModalOpen(true);

      console.log('Modal opened with data:', {
        id: invoice._id,
        status: invoice.status,
        total: invoice.totalAmount,
        payments: invoice.payments
      });
    } catch (error) {
      console.error('Error opening status modal:', error);
      showToast('Failed to open status modal', 'error');
    }
  };

  const handleConfirmStatusUpdate = async (newStatus, cancellationRemark = '') => {
    try {
      showLoading('Updating status...');

      if (newStatus === 'cancelled') {
        await updateSystemInvoiceStatus(selectedInvoiceId, newStatus, cancellationRemark);
        showToast('Invoice cancelled successfully', 'success');
      }

      setStatusModalOpen(false);
      setCancelledRem('');
      setSelectedInvoiceId(null);
      setSelectedInvoiceStatus('');
      setSelectedInvoiceTotal(0);
      setSelectedInvoicePayments([]);
      await fetchInvoices();
      hideLoading();
    } catch (error) {
      console.error('Error updating invoice status:', error);
      showToast(
        error.response?.data?.message || 'Failed to update invoice status',
        'error'
      );
      hideLoading();
    }
  };

  const submitPaymentDetails = async (invoiceId, paymentsArray) => {
    try {
      console.log('Submitting payment for system invoice:', invoiceId);
      console.log('Payments array:', paymentsArray);

      showLoading('Processing payment...');

      // Get receipt number from system settings
      const settingsResponse = await getSystemSettings();
      const receiptSettings = settingsResponse.data.receiptNo;
      const currentReceiptCount = receiptSettings.count;

      console.log('Current receipt count:', currentReceiptCount);

      // Count how many new payments need receipt IDs
      let newPaymentCount = 0;

      // Add receipt IDs to each payment that doesn't have one
      const paymentsWithReceipts = paymentsArray.map((payment) => {
        if (!payment.recieptId || payment.recieptId === 'PENDING') {
          newPaymentCount++;
          const receiptNum = currentReceiptCount + newPaymentCount;
          return {
            ...payment,
            recieptId: `${receiptSettings.text}/${receiptNum}`,
          };
        }
        return payment;
      });

      console.log('Payments with receipts:', paymentsWithReceipts);
      console.log('New payments count:', newPaymentCount);

      // Prepare payload with payments array
      const payload = {
        payments: paymentsWithReceipts
      };

      console.log('Final payload:', payload);

      // Add payment to system invoice
      await addPaymentToSystemInvoice(invoiceId, payload);

      // Increment receipt number ONLY for new payments
      for (let i = 0; i < newPaymentCount; i++) {
        await incrementReceiptNumber();
        console.log(`Incremented receipt number ${i + 1} of ${newPaymentCount}`);
      }

      showToast('Payment added successfully!', 'success');

      // Close modal and refresh
      setStatusModalOpen(false);
      setSelectedInvoiceId(null);
      setSelectedInvoiceStatus('');
      setSelectedInvoiceTotal(0);
      setSelectedInvoicePayments([]);
      setCancelledRem('');

      await fetchInvoices();
      hideLoading();
    } catch (error) {
      console.error('Error adding payment to system invoice:', error);
      showToast(
        error.response?.data?.message || 'Failed to add payment',
        'error'
      );
      hideLoading();
    }
  };

  const handleCancelStatusUpdate = () => {
    setStatusModalOpen(false);
    setSelectedInvoiceId(null);
    setCancelledRem('');
  };

  const getStatusOptions = () => {
    // If current status is cancelled, only allow viewing (no changes)
    if (selectedInvoiceStatus === 'cancelled') {
      return [{ label: 'Cancelled', value: 'cancelled' }];
    }

    const baseOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Cancelled', value: 'cancelled' },
      { label: 'Part Payment', value: 'part-payment' },
    ];

    // Only show "Paid" option if there are no existing payments
    if (!selectedInvoicePayments || selectedInvoicePayments.length === 0) {
      baseOptions.unshift({ label: 'Paid', value: 'paid' });
    }

    return baseOptions;
  };

  const handleCreateInvoice = () => {
    navigate(`/home/superadmin/create-system-invoice/${companyId}`);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      minAmount: '',
      maxAmount: '',
      fromDate: '',
      toDate: '',
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterOpen &&
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to prepare invoice settings with system defaults
  const prepareSystemInvoiceSettings = (company) => {
    return {
      headerText: company?.companyName || "System Invoice",
      footer: "",
      displayHeaderFooter: true,
      displayBankDetails: true,
      bankDetails: "<strong>HDFC Bank</strong> A/c No: <strong>08882020001068</strong> IFSC Code: <strong>HDFC0000888</strong> Branch: <strong>Ambawadi, Ahmedabad</strong>",
      isSignatureDigitalGenerated: false,
      displayTermsAndConditions: false,
      termsAndConditions: "",
      address: company ? `${company.address}, ${company.state} - ${company.zip}, ${company.country}` : "",
    };
  };
  
  const convertImageToDataURL = async (imagePath) => {
    try {
      const response = await fetch(imagePath);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to data URL:', error);
      return imagePath;
    }
  };

  const handlePreview = async (id) => {
    try {
      showLoading("Loading...");
      console.log("Starting PDF preview for system invoice:", id);

      // Fetch fresh invoice data from API
      const response = await getSystemInvoicesByCompany(companyId);
      const allInvoices = response.data?.filteredInvoice || [];
      const invoice = allInvoices.find(inv => inv._id === id);

      if (!invoice) {
        showToast("Invoice not found", "error");
        hideLoading();
        return;
      }

      // Get company details for the invoice settings
      const companyResponse = await getCompanyByID(companyId);
      const company = companyResponse.data?.company;

      const headerDataURL = await convertImageToDataURL(systemInvoiceHeader);
      const settings = {
        ...prepareSystemInvoiceSettings(company),
        header: headerDataURL,
      }

      console.log("System invoice data:", invoice);
      console.log("Company data:", company);
      console.log("Settings data:", settings);

      const container = document.createElement("div");
      container.id = "pdf-preview-container";
      container.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: 794px;
        min-height: auto;
        background: white;
        padding: 0;
        margin: 0;
        font-family: Arial, sans-serif;
        z-index: 9999;
        box-sizing: border-box;
        overflow: visible;
        display: block;
      `;

      document.body.appendChild(container);

      // Create root and render
      const root = ReactDOM.createRoot(container);

      // Render the NEW SystemInvoicePDFTemplate component
      root.render(
        <div
          style={{
            width: "794px",
            minHeight: "auto",
            backgroundColor: "white",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        >
          <Systeminvoicepdftemplate
            invoice={invoice} 
            settings={settings}
            companyDetails={company}
          />
        </div>
      );

      console.log("Component rendered, waiting for content to load...");

      // Wait for React to render and calculate actual height
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Wait for all images to load
      const allImages = container.querySelectorAll("img");
      console.log("Found images:", allImages.length);

      if (allImages.length > 0) {
        await Promise.all(
          Array.from(allImages).map((img, index) => {
            return new Promise((resolve) => {
              console.log(`Checking image ${index + 1}:`, img.src);

              if (img.complete && img.naturalWidth > 0) {
                console.log(`Image ${index + 1} already loaded`);
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  console.warn(`Image ${index + 1} load timeout:`, img.src);
                  resolve();
                }, 8000);

                img.onload = () => {
                  console.log(`Image ${index + 1} loaded successfully`);
                  clearTimeout(timeout);
                  resolve();
                };

                img.onerror = () => {
                  console.error(`Image ${index + 1} failed to load:`, img.src);
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          })
        );
      }

      // Get the actual content height after rendering
      const actualHeight = Math.max(
        container.scrollHeight,
        container.offsetHeight,
        1123
      );

      // Update container with actual dimensions - position in viewport but behind everything
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 794px;
        height: ${actualHeight}px;
        background: white;
        padding: 0;
        margin: 0;
        font-family: Arial, sans-serif;
        z-index: -9999;
        box-sizing: border-box;
        overflow: visible;
        display: block;
      `;

      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Content loaded, generating PDF for preview...");

      if (!container.innerHTML.trim()) {
        throw new Error("Container is empty - no content to convert to PDF");
      }

      const pdfWidth = 8.27;
      const pdfHeight = Math.max(11.69, actualHeight / 96);

      const options = {
        margin: 0,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
          width: 794,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: actualHeight,
        },
        jsPDF: {
          unit: "in",
          format: [pdfWidth, pdfHeight],
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const html2pdf = (await import("html2pdf.js")).default;

      // Generate PDF as blob instead of downloading
      const pdfBlob = await html2pdf()
        .set(options)
        .from(container)
        .outputPdf("blob");

      // Create blob URL and open in new tab
      const blobUrl = URL.createObjectURL(pdfBlob);
      const previewWindow = window.open(blobUrl, "_blank");

      if (!previewWindow) {
        showToast("Please allow popups for preview functionality", "error");
        URL.revokeObjectURL(blobUrl);
        return;
      }

      console.log("PDF preview opened successfully!");

      // Clean up the blob URL after some time
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 60000); // Clean up after 1 minute

      // Clean up DOM
      setTimeout(() => {
        try {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      showToast(`Failed to generate preview: ${error.message}`, "error");

      const container = document.getElementById("pdf-preview-container");
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }
    } finally {
      hideLoading();
    }
  };

  const handleDownload = async (id) => {
    try {
      showLoading("Loading...");
      console.log("Starting PDF generation for system invoice:", id);

      // Fetch fresh invoice data from API
      const response = await getSystemInvoicesByCompany(companyId);
      const allInvoices = response.data?.filteredInvoice || [];
      const invoice = allInvoices.find(inv => inv._id === id);

      if (!invoice) {
        showToast("Invoice not found", "error");
        hideLoading();
        return;
      }

      // Get company details for the invoice settings
      const companyResponse = await getCompanyByID(companyId);
      const company = companyResponse.data?.company;
      console.log("Waiting for Verdana font to load...");
      // This tells the browser to wait until the font 'Verdana' is loaded
      await document.fonts.load('1em Verdana');
      console.log("Verdana font loaded successfully!");
      const headerDataURL = await convertImageToDataURL(systemInvoiceHeader);
      const settings = {
        ...prepareSystemInvoiceSettings(company),
        header: headerDataURL,
      };

      console.log("System invoice data:", invoice);
      console.log("Company data:", company);
      console.log("Settings data:", settings);

      const container = document.createElement("div");
      container.id = "pdf-download-container";
      container.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: 794px;
        min-height: auto;
        background: white;
        padding: 0;
        margin: 0;
        font-family: Arial, sans-serif;
        z-index: 9999;
        box-sizing: border-box;
        overflow: visible;
        display: block;
      `;

      document.body.appendChild(container);

      // Create root and render
      const root = ReactDOM.createRoot(container);

      // Render the NEW SystemInvoicePDFTemplate component
      root.render(
        <div
          style={{
            width: "794px",
            minHeight: "auto",
            backgroundColor: "white",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        >
          <Systeminvoicepdftemplate
            invoice={invoice} 
            settings={settings}
            companyDetails={company}
          />
        </div>
      );

      console.log("Component rendered, waiting for content to load...");

      // Wait for React to render and calculate actual height
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Wait for all images to load
      const allImages = container.querySelectorAll("img");
      console.log("Found images:", allImages.length);

      if (allImages.length > 0) {
        await Promise.all(
          Array.from(allImages).map((img, index) => {
            return new Promise((resolve) => {
              console.log(`Checking image ${index + 1}:`, img.src);

              if (img.complete && img.naturalWidth > 0) {
                console.log(`Image ${index + 1} already loaded`);
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  console.warn(`Image ${index + 1} load timeout:`, img.src);
                  resolve();
                }, 8000);

                img.onload = () => {
                  console.log(`Image ${index + 1} loaded successfully`);
                  clearTimeout(timeout);
                  resolve();
                };

                img.onerror = () => {
                  console.error(`Image ${index + 1} failed to load:`, img.src);
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          })
        );
      }

      // Get the actual content height after rendering
      const actualHeight = Math.max(
        container.scrollHeight,
        container.offsetHeight,
        1123
      );

      // Update container with actual dimensions - position in viewport but behind everything
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 794px;
        height: ${actualHeight}px;
        background: white;
        padding: 0;
        margin: 0;
        font-family: Arial, sans-serif;
        z-index: -9999;
        box-sizing: border-box;
        overflow: visible;
        display: block;
      `;

      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Content loaded, generating PDF...");

      if (!container.innerHTML.trim()) {
        throw new Error("Container is empty - no content to convert to PDF");
      }

      const pdfWidth = 8.27;
      const pdfHeight = Math.max(11.69, actualHeight / 96);

      const options = {
        margin: 0,
        filename: `${invoice.invoiceNumber || "system-invoice"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
          width: 794,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: actualHeight,
        },
        jsPDF: {
          unit: "in",
          format: [pdfWidth, pdfHeight],
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const html2pdf = (await import("html2pdf.js")).default;

      await html2pdf().set(options).from(container).save();

      console.log("PDF generated successfully!");
      showToast("Invoice downloaded successfully!", "success");

      // Clean up
      setTimeout(() => {
        try {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      showToast(`Failed to generate PDF: ${error.message}`, "error");

      const container = document.getElementById("pdf-download-container");
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }
    } finally {
      hideLoading();
    }
  };

  const handleDownloadReceipt = async (payment, invoiceId) => {
    try {
      showLoading("Generating receipt PDF...");
      console.log("Starting receipt PDF generation for payment:", payment);

      // Get full invoice details from API
      const response = await getSystemInvoicesByCompany(companyId);
      const allInvoices = response.data?.filteredInvoice || [];
      const invoice = allInvoices.find(inv => inv._id === invoiceId);

      if (!invoice) {
        showToast("Invoice not found", "error");
        hideLoading();
        return;
      }

      // Get company details for the receipt settings
      const companyResponse = await getCompanyByID(companyId);
      const company = companyResponse.data?.company;

      console.log("Waiting for Verdana font to load...");

      // Convert header image to data URL for receipt
      const headerDataURL = await convertImageToDataURL(systemInvoiceHeader);

      const settings = {
        ...prepareSystemInvoiceSettings(company),
        header: headerDataURL, // Add the converted header image
      };

      console.log("Invoice data for receipt:", invoice);
      console.log("Company data:", company);
      console.log("Settings data for receipt:", settings);

      const container = document.createElement("div");
      container.id = "receipt-pdf-container";
      container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: 794px;
      min-height: auto;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: 9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      document.body.appendChild(container);

      // Create root and render
      const root = ReactDOM.createRoot(container);

      // Render the receipt component with system receipt flag
      root.render(
        <div
          style={{
            width: "794px",
            minHeight: "auto",
            backgroundColor: "white",
            display: "block",
            margin: 0,
            padding: 0,
          }}
        >
          <SystemReceiptPdfTemplate
            payment={payment}
            invoice={invoice}
            settings={settings}
            companyDetails={company}
          />
        </div>
      );

      console.log("Receipt component rendered, waiting for content to load...");

      // Wait for React to render and calculate actual height
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Wait for all images to load
      const allImages = container.querySelectorAll("img");
      console.log("Found images in receipt:", allImages.length);

      if (allImages.length > 0) {
        await Promise.all(
          Array.from(allImages).map((img, index) => {
            return new Promise((resolve) => {
              console.log(`Checking receipt image ${index + 1}:`, img.src);

              if (img.complete && img.naturalWidth > 0) {
                console.log(`Receipt image ${index + 1} already loaded`);
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  console.warn(
                    `Receipt image ${index + 1} load timeout:`,
                    img.src
                  );
                  resolve();
                }, 8000);

                img.onload = () => {
                  console.log(`Receipt image ${index + 1} loaded successfully`);
                  clearTimeout(timeout);
                  resolve();
                };

                img.onerror = () => {
                  console.error(
                    `Receipt image ${index + 1} failed to load:`,
                    img.src
                  );
                  clearTimeout(timeout);
                  resolve();
                };
              }
            });
          })
        );
      }

      // Get the actual content height after rendering
      const actualHeight = Math.max(
        container.scrollHeight,
        container.offsetHeight,
        800 // Minimum height for receipt
      );

      // Update container with actual dimensions - position in viewport but behind everything
      container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 794px;
      height: ${actualHeight}px;
      background: white;
      padding: 0;
      margin: 0;
      font-family: Arial, sans-serif;
      z-index: -9999;
      box-sizing: border-box;
      overflow: visible;
      display: block;
    `;

      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Receipt content loaded, generating PDF...");

      if (!container.innerHTML.trim()) {
        throw new Error("Container is empty - no content to convert to PDF");
      }

      const pdfWidth = 8.27;
      const pdfHeight = Math.max(11.69, actualHeight / 96);

      const options = {
        margin: 0,
        filename: `Receipt_${payment.recieptId || "Unknown"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: true,
          width: 794,
          height: actualHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: actualHeight,
        },
        jsPDF: {
          unit: "in",
          format: [pdfWidth, pdfHeight],
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      const html2pdf = (await import("html2pdf.js")).default;

      await html2pdf().set(options).from(container).save();

      console.log("Receipt PDF generated successfully!");
      showToast("Receipt downloaded successfully!", "success");

      // Clean up
      setTimeout(() => {
        try {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        } catch (cleanupError) {
          console.warn("Receipt cleanup error:", cleanupError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error generating receipt PDF:", error);
      showToast(`Failed to generate receipt: ${error.message}`, "error");

      const container = document.getElementById("receipt-pdf-container");
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (cleanupError) {
          console.warn("Cleanup error:", cleanupError);
        }
      }
    } finally {
      hideLoading();
    }
  };

  return (
    <div className="system-invoices-container">
      <div className="system-invoices-header">
        <div className="header-left">
          <h2>System Invoices</h2>
          {companyData && (
            <p className="company-name">{companyData.company?.companyName}</p>
          )}
        </div>
        <div className="header-buttons">
          {/* Debug: Show filter state */}
          {/* <span style={{ fontSize: '12px', color: 'red' }}>Filter Open: {isFilterOpen ? 'YES' : 'NO'}</span> */}
          
          <div className="filter-dropdown-wrapper">
            <button
              ref={filterButtonRef}
              className="filter-toggle-btn"
              onClick={() => {
                console.log('Filter button clicked, current state:', isFilterOpen);
                setIsFilterOpen(!isFilterOpen);
              }}
            >
              <FaFilter /> {isFilterOpen ? 'Hide Filters' : 'Filters'}
            </button>
            
            {isFilterOpen && (
              <div
                ref={filterDropdownRef}
                className="filter-dropdown-box"
              >
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="part-payment">Part Payment</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Min Total</label>
                  <input
                    type="number"
                    placeholder="Min Amount"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label>Max Total</label>
                  <input
                    type="number"
                    placeholder="Max Amount"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  />
                </div>

                <div className="filter-actions">
                  <button
                    className="clear-btn"
                    onClick={() => {
                      clearAllFilters();
                    }}
                  >
                    Clear
                  </button>
                  <button
                    className="hide-btn"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Hide
                  </button>
                </div>
              </div>
            )}
          </div>

          <button className="create-invoice-btn" onClick={handleCreateInvoice}>
            <FaPlus /> Create Invoice
          </button>
        </div>
      </div>

      <div className="invoices-table-wrapper">
        {invoices.length === 0 ? (
          <div className="no-data-message">
            <p>No system invoices found for this company</p>
            {/* <button className="create-first-btn" onClick={handleCreateInvoice}>
              Create First Invoice
            </button> */}
          </div>
        ) : (
          <table className="invoices-table">
            <thead>
              <tr>
                <th>Invoice No.</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total Amount</th>
                <th>Paid Amount</th>
                <th>Pending Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const totalPaid = invoice.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
                const pendingAmount = invoice.totalAmount - totalPaid;

                return (
                  <tr key={invoice._id}>
                    <td className="invoice-number">{invoice.invoiceNumber}</td>
                    <td>{formatDate(invoice.createdAt)}</td>
                    <td>
                      <div className="items-cell">
                        {invoice.items?.map((item, idx) => (
                          <div key={idx} className="item-row">
                            <span className="item-name">{item.name}</span>
                            {item.description && (
                              <span className="item-desc">{item.description}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="amount">₹{invoice.totalAmount?.toFixed(2)}</td>
                    <td className="amount paid">₹{totalPaid.toFixed(2)}</td>
                    <td className="amount pending">₹{pendingAmount.toFixed(2)}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn preview-btn"
                        onClick={() => handlePreview(invoice._id)}
                        title="Preview Invoice"
                      >
                       Preview
                      </button>
                      <button
                        className="action-btn download-btn"
                        onClick={() => handleDownload(invoice._id)}
                        title="Download Invoice"
                      >
                        Download
                      </button>
                      <button
                        className="action-btn update-status-btn"
                        onClick={() => handleStatusUpdateClick(invoice._id)}
                        title={invoice.status === 'cancelled' ? 'View Cancellation Details' : 'Update Status / Add Payment'}
                      >
                        Update Status
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {statusModalOpen && (
        <ConfirmationModal
          isOpen={statusModalOpen}
          message="Update invoice status or add payment"
          onConfirm={handleConfirmStatusUpdate}
          onCancel={handleCancelStatusUpdate}
          options={getStatusOptions()}
          invoiceId={selectedInvoiceId}
          currentStatus={selectedInvoiceStatus}
          totalAmount={selectedInvoiceTotal}
          previousPayments={selectedInvoicePayments}
          submitPaymentDetails={submitPaymentDetails}
          activePage="systemInvoices"
          cancelledRem={cancelledRem}
          handleDownloadReceipt={handleDownloadReceipt}
        />
      )}
    </div>
  );
};